<template>
  <v-card class="error-card">
    <v-card-title class="error-header">
      <v-icon left color="white">mdi-alert-circle</v-icon>
      {{ isMetadataFormat ? 'Error Details' : 'Error Messages' }}
      <v-spacer></v-spacer>
      <v-chip v-if="!isMetadataFormat" color="white" text-color="#384557" small>
        {{ errorCount }} errors
      </v-chip>
    </v-card-title>
    <div class="scrollable-container">
      <!-- Object format (rule_import) with key-values -->
      <div v-if="isMetadataFormat" class="metadata-content">
        <v-row dense>
          <v-col cols="12">
            <div class="metadata-item"><strong>Message:</strong> {{ errorMessages.message }}</div>
          </v-col>
          <v-col
            v-if="errorMessages.total_count || errorMessages.total_count === 0"
            cols="6"
            sm="4"
          >
            <div class="metadata-item">
              <strong>Total Count:</strong> {{ errorMessages.total_count | number }}
            </div>
          </v-col>
          <v-col
            v-if="errorMessages.failed_count || errorMessages.failed_count === 0"
            cols="6"
            sm="4"
          >
            <div class="metadata-item">
              <strong>Failed Count:</strong> {{ errorMessages.failed_count | number }}
            </div>
          </v-col>
          <v-col
            v-if="errorMessages.processed_count || errorMessages.processed_count === 0"
            cols="6"
            sm="4"
          >
            <div class="metadata-item">
              <strong>Processed Count:</strong> {{ errorMessages.processed_count | number }}
            </div>
          </v-col>
          <v-col
            v-if="errorMessages.total_imports || errorMessages.total_imports === 0"
            cols="6"
            sm="4"
          >
            <div class="metadata-item">
              <strong>Total Count:</strong> {{ errorMessages.total_imports | number }}
            </div>
          </v-col>
          <v-col
            v-if="errorMessages.failed_imports || errorMessages.failed_imports === 0"
            cols="6"
            sm="4"
          >
            <div class="metadata-item">
              <strong>Failed Count:</strong> {{ errorMessages.failed_imports | number }}
            </div>
          </v-col>
          <v-col
            v-if="errorMessages.successful_imports || errorMessages.successful_imports === 0"
            cols="6"
            sm="4"
          >
            <div class="metadata-item">
              <strong>Processed Count:</strong> {{ errorMessages.successful_imports | number }}
            </div>
          </v-col>
          <v-col v-if="errorMessages.source_tree_id" cols="6" sm="4">
            <div class="metadata-item">
              <strong>Source Tree ID:</strong> {{ errorMessages.source_tree_id }}
            </div>
          </v-col>
          <v-col v-if="errorMessages.target_tree_id" cols="6" sm="4">
            <div class="metadata-item">
              <strong>Target Tree ID:</strong> {{ errorMessages.target_tree_id }}
            </div>
          </v-col>
          <v-col v-if="errorMessages.target_localization" cols="6" sm="4">
            <div class="metadata-item">
              <strong>Target Market:</strong> {{ errorMessages.target_localization }}
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Array format (bulk_upload) with pagination -->
      <div v-else>
        <v-alert v-if="isTruncated" type="warning" dense text class="ma-2">
          Showing first {{ maxErrors }} error patterns. Total: {{ errorCount }}
        </v-alert>
        <v-expansion-panels accordion flat>
          <v-expansion-panel v-for="(errors, pattern, index) in limitedErrorMessages" :key="index">
            <v-expansion-panel-header class="error-pattern">
              {{ pattern }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-for="(error, i) in limitErrors(errors)" :key="i" class="error-message">
                <v-icon small color="#ffcdd2" class="mr-2">mdi-close-circle</v-icon>
                {{ error }}
              </div>
              <div v-if="errors.length > maxErrorsPerPattern" class="error-message">
                <v-icon small color="#ffcdd2" class="mr-2">mdi-information</v-icon>
                ... and {{ errors.length - maxErrorsPerPattern }} more errors
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'ErrorMessageDisplay',
  filters: {
    number(value) {
      if (value === null || value === undefined) return '';
      return value.toLocaleString();
    },
  },
  props: {
    errorMessages: {
      type: Object,
      required: true,
    },
    maxErrors: {
      type: Number,
      default: 50,
    },
    maxErrorsPerPattern: {
      type: Number,
      default: 100,
    },
  },
  computed: {
    isMetadataFormat() {
      // Check if error_message is in metadata format (rule_import)
      // vs array format (bulk_upload)
      if (!this.errorMessages) return false;
      // If it does not have specified key, it's the rule_import format
      return this.errorMessages['Error while validating File'] === undefined;
    },
    errorCount() {
      return Object.keys(this.errorMessages).length;
    },
    limitedErrorMessages() {
      if (this.isMetadataFormat) return this.errorMessages;
      const entries = Object.entries(this.errorMessages);
      return Object.fromEntries(entries.slice(0, this.maxErrors));
    },
    isTruncated() {
      return !this.isMetadataFormat && this.errorCount > this.maxErrors;
    },
  },
  methods: {
    limitErrors(errors) {
      if (Array.isArray(errors)) {
        return errors.slice(0, this.maxErrorsPerPattern);
      }
      return errors;
    },
  },
};
</script>

<style scoped>
.scrollable-container {
  height: max-content;
  max-height: max-content;
  overflow-y: auto;
  padding: 0;
  background: #384557;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  color: #fff;
}

.error-card {
  width: 100%;
  background: transparent !important;
}

.error-header {
  background: #d32f2f !important;
  color: white !important;
  font-weight: bold;
  font-size: 18px;
}

.error-pattern {
  color: white;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.1);
}

.error-message {
  padding: 8px 0;
  color: #ffcdd2;
}

.metadata-content {
  padding: 16px;
  color: white;
}

.metadata-item {
  padding: 8px 0;
  color: #fff;
}

.metadata-item strong {
  color: #ffcdd2;
  display: inline-block;
  margin-right: 8px;
}

/* Override Vuetify expansion panel styles */
.v-expansion-panels {
  background: transparent !important;
}

.v-expansion-panel {
  background: transparent !important;
  margin-bottom: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.v-expansion-panel-header {
  padding: 12px 16px;
}

.v-expansion-panel-content__wrap {
  padding: 0 16px 12px;
}
</style>
